import { defaultCurrency } from 'app/utils/constants/intl.constants'
import {
  hasCollectShippingType,
  hasShipOnlyShippingType,
  isCollectShippingType
} from 'app/utils/helpers/shipping.helpers'
import { transformAddressObjectToString } from 'app/utils/helpers/transforms.helper'

import { formatLocation } from '../../helpers/listing.helpers'
import useShippingCost from '../../hooks/useShippingCost'
import {
  getShippingConfigsAvailability,
  getShippingScopeAvailability,
  resolveListingReturns,
  resolveListingShippingMethods
} from '../helpers/shippingReturnRules.helpers'

interface UseShippingReturnRulesProps {
  shipping?: ShippingModel
  shippingType: string[]
  isPreview?: boolean
  isAuthUserListing?: boolean
  slug: string
  isPersonalSeller?: boolean
  ownShippingCurrency?: string
}

export const useShippingReturnRules = ({
  shipping,
  shippingType,
  isPreview,
  isAuthUserListing,
  slug,
  isPersonalSeller,
  ownShippingCurrency
}: UseShippingReturnRulesProps) => {
  const isClickAndCollectOnly = isCollectShippingType(shippingType)
  const hasShipShipping = hasShipOnlyShippingType(shippingType)
  const hasClickAndCollect = hasCollectShippingType(shippingType)

  const shippingMethods = resolveListingShippingMethods(shipping)
  const hasIntegratedShipping = shippingMethods.some(
    (method) => method.method === 'integrated' && !method.isFree
  )

  const { isInternationalEnabled } = getShippingConfigsAvailability(shipping)

  const shippingScope = getShippingScopeAvailability({
    shipping,
    shippingTypes: shippingType
  })

  const returnsInfo = resolveListingReturns(shipping)

  const showAddressActions =
    !isPreview &&
    !isClickAndCollectOnly &&
    !isAuthUserListing &&
    hasIntegratedShipping

  const isShippingCostEnabled =
    !isPreview &&
    !isAuthUserListing &&
    !isClickAndCollectOnly &&
    hasIntegratedShipping

  const {
    shippingCost,
    addresses,
    calculateShippingCost,
    isSeveralAddresses,
    isNoAddress
  } = useShippingCost({ slug, enabled: isShippingCostEnabled })

  const addressOptions = addresses.map((item) => {
    const address = transformAddressObjectToString(item)

    return { id: item.id, address }
  })

  const collectionLocation = formatLocation({
    addressObj: shipping,
    key: 'collection_address',
    withoutZipCode: isPersonalSeller
  })

  const country =
    shipping?.dispatch_address?.full_country_name ||
    shipping?.dispatch_address?.country

  return {
    addressOptions,
    isSeveralAddresses,
    isNoAddress,
    collectionLocation,
    country,
    calculateShippingCost,
    isInternationalEnabled,
    shippingScope,
    shippingMethods,
    hasIntegratedShipping,
    ownShippingCurrency: ownShippingCurrency || defaultCurrency,
    returnsInfo,
    hasShipShipping,
    hasClickAndCollect,
    showAddressActions,
    shippingCost
  }
}
